import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useRole } from "@/contexts/RoleContext";
import { startFmSession, answerFmQuestion } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CheckCircle2, XCircle, TrendingUp, TrendingDown, Lock, Trophy, BookOpen, Loader2, RotateCcw } from "lucide-react";
import QuestionFlagTrigger from "@/components/QuestionFlagTrigger";
import SolveWithMitraa from "@/components/SolveWithMitraa";

// ── Digital Notebook Review templates ─────────────────────────────────────────
const DNR = {
  "A-RT1": { title: "Number Line Method", tip: "Start at the bigger number and count forward. Use your fingers or draw jumps on a number line." },
  "A-RT2": { title: "Regrouping (Carrying)", tip: "When the ones column adds up to 10 or more, carry 1 to the tens column. Write the leftover in the ones place." },
  "A-RT3": { title: "Column Addition", tip: "Line up digits by place value: ones under ones, tens under tens. Always add from right to left." },
  "S-RT1": { title: "Count Back Method", tip: "Start at the bigger number. Count backwards by the smaller number. Each step back is one subtraction." },
  "S-RT2": { title: "Regrouping (Borrowing)", tip: "When the ones digit on top is too small, borrow 1 from the tens. The tens digit decreases by 1." },
  "S-RT3": { title: "Number Bonds", tip: "Think of subtraction as a missing addend. If 5 − 3 = ?, ask: 3 + ? = 5. Count up from 3 to reach 5." },
  "S-RT4": { title: "Multi-step Subtraction", tip: "Break the number you are subtracting into smaller parts. Subtract one part at a time." },
  "M-RT1": { title: "Repeated Addition", tip: "Multiplication is repeated addition. 4 × 3 means 3 + 3 + 3 + 3. Draw equal groups to visualise." },
  "M-RT2": { title: "Times Tables", tip: "Use the table family to recall the fact quickly. Write the table out: 2×1=2, 2×2=4, …, 2×10=20." },
  "M-RT3": { title: "Column Multiplication", tip: "Multiply each digit separately, then add the partial products. Always multiply ones first, then tens." },
  "M-RT4": { title: "Carrying in Multiplication", tip: "If the product of a digit exceeds 9, write the ones and carry the tens to the next column." },
  "M-RT5": { title: "Multi-digit × Single digit", tip: "Multiply each digit of the top number by the bottom digit from right to left. Carry when needed." },
  "D-RT1": { title: "Equal Sharing", tip: "Division means sharing equally. Ask: how many groups of the divisor fit into the dividend?" },
  "D-RT2": { title: "Times-table Link", tip: "Division is the inverse of multiplication. 20 ÷ 4 = ? Think: 4 × ? = 20." },
  "D-RT3": { title: "Short Division", tip: "Write the dividend under the bar, divisor outside. Divide digit by digit from left to right. Carry remainders." },
  "D-RT4": { title: "Remainders", tip: "When the dividend does not divide evenly, the leftover is the remainder. Write it as R after the quotient." },
  "D-RT5": { title: "Long Division Steps", tip: "Follow D-M-S-B: Divide → Multiply → Subtract → Bring down. Repeat until no digits remain." },
  "D-RT6": { title: "Multi-digit Divisors", tip: "Estimate how many times the divisor fits into the first few digits of the dividend. Adjust if needed." },
  "D-RT7": { title: "Dividing Larger Numbers", tip: "Work through the dividend in chunks. Keep track of your partial quotients to assemble the final answer." },
};

const DEFAULT_DNR = { title: "Think It Through", tip: "Read the question carefully. Identify what operation is needed and apply it step by step." };

// ── Skill symbol map ───────────────────────────────────────────────────────────
const SKILL_SYMBOL = { Addition: "+", Subtraction: "−", Multiplication: "×", Division: "÷" };

// ── Helper ─────────────────────────────────────────────────────────────────────
function StreakDots({ streak }) {
  return (
    <div className="flex gap-1.5 items-center">
      {[0, 1, 2].map(i => (
        <div
          key={i}
          className={`w-3 h-3 rounded-full transition-all ${i < streak ? "bg-fuchsia-500 scale-110" : "bg-fuchsia-200"}`}
        />
      ))}
    </div>
  );
}

function LevelPill({ level }) {
  return (
    <span className="text-xs font-bold px-3 py-1 rounded-full bg-fuchsia-100 text-fuchsia-700 border border-fuchsia-200">
      {level}
    </span>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function FMSessionRunner() {
  const { skill } = useParams();
  const { learner } = useRole();
  const nav = useNavigate();

  const [phase, setPhase] = useState("loading"); // loading | l0_question | l0_result | practice_question | show_review | level_up | level_down | l0_gate_retry | locked | mastered | error
  const [sessionId, setSessionId] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [pendingQuestion, setPendingQuestion] = useState(null); // held during review / transition
  const [answer, setAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null); // last API response
  const [streak, setStreak] = useState(0);
  const [currentLevel, setCurrentLevel] = useState(null);
  const [l0Progress, setL0Progress] = useState({ idx: 0, total: 3, correct: 0 });
  const [errorMsg, setErrorMsg] = useState("");
  const [showNotebook, setShowNotebook] = useState(false);
  const questionStartRef = useRef(null); // timestamp when current question was shown

  const inputRef = useRef(null);

  const decodedSkill = decodeURIComponent(skill);
  const symbol = SKILL_SYMBOL[decodedSkill] || "?";

  // Focus input & start timer whenever a question is shown
  useEffect(() => {
    if (phase.includes("question")) {
      questionStartRef.current = Date.now();
      if (inputRef.current) inputRef.current.focus();
    }
  }, [phase, currentQuestion]);

  // Start session on mount
  useEffect(() => {
    if (!learner?.learner_id) return;
    startFmSession(learner.learner_id, decodedSkill)
      .then(data => {
        setSessionId(data.session_id);
        setCurrentLevel(data.current_level);
        setCurrentQuestion(data.question);
        if (data.phase === "l0_gate") {
          setL0Progress({ idx: 0, total: 3, correct: 0 });
          setPhase("l0_question");
        } else {
          setStreak(data.streak || 0);
          setPhase("practice_question");
        }
      })
      .catch(err => {
        const detail = err?.response?.data?.detail || "";
        if (detail === "skill_locked") setPhase("locked");
        else if (detail === "skill_mastered") setPhase("mastered");
        else if (detail === "skill_not_unlocked") setPhase("not_unlocked");
        else {
          setErrorMsg(detail || "Could not start session.");
          setPhase("error");
        }
      });
  }, [learner, decodedSkill]);

  const handleSubmit = async () => {
    if (!answer.trim() || submitting) return;
    setSubmitting(true);
    const timeTakenMs = questionStartRef.current ? Date.now() - questionStartRef.current : null;
    try {
      const res = await answerFmQuestion(sessionId, answer.trim(), timeTakenMs);
      setLastResult(res);
      setAnswer("");

      switch (res.next_action) {
        case "l0_next_question":
          setL0Progress({ idx: res.l0_idx, total: 3, correct: res.l0_correct_so_far });
          setCurrentQuestion(res.next_question);
          setPhase("l0_question");
          break;

        case "l0_pass":
          setL0Progress(p => ({ ...p, correct: res.l0_score }));
          setPendingQuestion(res.next_question);
          setCurrentLevel(res.new_level);
          setPhase("l0_result");
          break;

        case "l0_fail":
          setPhase("l0_result");
          break;

        case "l0_gate_retry":
          setPendingQuestion(res.next_question);
          setPhase("l0_gate_retry");
          break;

        case "next_question":
          setStreak(res.streak || 0);
          setCurrentQuestion(res.next_question);
          setPhase("practice_question");
          break;

        case "show_review":
          setPendingQuestion(res.next_question);
          setPhase("show_review");
          break;

        case "level_up":
          setCurrentLevel(res.new_level);
          setStreak(0);
          setPendingQuestion(res.next_question);
          setPhase("level_up");
          break;

        case "level_down":
          setCurrentLevel(res.new_level);
          setStreak(0);
          setPendingQuestion(res.next_question);
          setPhase("level_down");
          break;

        case "mastered":
          setPhase("mastered");
          break;

        case "locked":
          setPhase("locked");
          break;

        default:
          break;
      }
    } catch {
      setErrorMsg("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const proceedFromReview = () => {
    setCurrentQuestion(pendingQuestion);
    setPendingQuestion(null);
    const isL0 = pendingQuestion?.level?.endsWith("0");
    setPhase(isL0 ? "l0_question" : "practice_question");
  };

  const proceedFromTransition = () => {
    setCurrentQuestion(pendingQuestion);
    setPendingQuestion(null);
    setPhase("practice_question");
  };

  const startL0FromRetry = () => {
    setCurrentQuestion(pendingQuestion);
    setPendingQuestion(null);
    setL0Progress({ idx: 0, total: 3, correct: 0 });
    setPhase("l0_question");
  };

  const proceedAfterL0Pass = () => {
    setCurrentQuestion(pendingQuestion);
    setPendingQuestion(null);
    setStreak(0);
    setPhase("practice_question");
  };

  // ── Keyboard handler
  const onKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  // ── RENDER ────────────────────────────────────────────────────────────────────

  if (phase === "loading") {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-fuchsia-400 animate-spin" />
      </div>
    );
  }

  if (phase === "not_unlocked") {
    return (
      <TerminalScreen
        icon={<Lock className="w-10 h-10 text-slate-400" />}
        title="Not Unlocked Yet"
        message="Your teacher hasn't unlocked this skill for you. Check back after your teacher opens it."
        color="slate"
      >
        <Link to="/math-foundations"><Button variant="outline" className="rounded-full">Back to Math Foundations</Button></Link>
      </TerminalScreen>
    );
  }

  if (phase === "locked") {
    return (
      <TerminalScreen
        icon={<Lock className="w-10 h-10 text-red-400" />}
        title="Skill Locked"
        message={lastResult?.lock_reason === "consecutive_level_down_lock"
          ? "You've had a few tough rounds — your teacher will review and unlock you."
          : "This skill is locked. Your teacher will help you review and try again."}
        color="red"
      >
        <Link to="/math-foundations"><Button variant="outline" className="rounded-full">Back to Math Foundations</Button></Link>
      </TerminalScreen>
    );
  }

  if (phase === "mastered") {
    return (
      <TerminalScreen
        icon={<Trophy className="w-10 h-10 text-amber-400" />}
        title="Skill Mastered!"
        message={`You've mastered ${decodedSkill}. Brilliant work — keep practising to stay sharp!`}
        color="amber"
      >
        <Link to="/math-foundations"><Button className="rounded-full bg-amber-500 hover:bg-amber-600 text-white">Back to Math Foundations</Button></Link>
      </TerminalScreen>
    );
  }

  if (phase === "error") {
    return (
      <TerminalScreen
        icon={<XCircle className="w-10 h-10 text-red-400" />}
        title="Something went wrong"
        message={errorMsg}
        color="red"
      >
        <Link to="/math-foundations"><Button variant="outline" className="rounded-full">Go Back</Button></Link>
      </TerminalScreen>
    );
  }

  // ── L0 GATE ──────────────────────────────────────────────────────────────────
  if (phase === "l0_question") {
    return (
      <SessionLayout skill={decodedSkill} symbol={symbol} onBack={() => nav("/math-foundations")}>
        {/* Gate header */}
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-bold uppercase tracking-wider">
            Gate Check · Question {l0Progress.idx + 1} of {l0Progress.total}
          </div>
          <p className="text-slate-500 text-xs mt-1.5">Answer 2 out of 3 correctly to unlock practice.</p>
        </div>

        <QuestionCard question={currentQuestion} />

        <AnswerInput
          value={answer}
          onChange={setAnswer}
          onKeyDown={onKeyDown}
          onSubmit={handleSubmit}
          submitting={submitting}
          inputRef={inputRef}
        />
      </SessionLayout>
    );
  }

  if (phase === "l0_result") {
    const passed = lastResult?.next_action === "l0_pass";
    return (
      <SessionLayout skill={decodedSkill} symbol={symbol} onBack={() => nav("/math-foundations")}>
        <div className={`rounded-3xl p-8 text-center ${passed ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"}`}>
          {passed
            ? <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
            : <XCircle className="w-14 h-14 text-red-400 mx-auto mb-4" />
          }
          <h2 className={`font-display text-2xl font-bold mb-2 ${passed ? "text-emerald-700" : "text-red-600"}`}>
            {passed ? "Gate Passed!" : "Not quite yet"}
          </h2>
          <p className="text-slate-600 text-sm mb-6">{lastResult?.message}</p>
          {passed
            ? <Button onClick={proceedAfterL0Pass} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-8">Start Practice</Button>
            : <Link to="/math-foundations"><Button variant="outline" className="rounded-full">Back to Math Foundations</Button></Link>
          }
        </div>
      </SessionLayout>
    );
  }

  // ── PRACTICE QUESTION ─────────────────────────────────────────────────────────
  if (phase === "practice_question") {
    return (
      <SessionLayout skill={decodedSkill} symbol={symbol} onBack={() => nav("/math-foundations")}>
        {/* Level + streak */}
        <div className="flex items-center justify-between mb-6">
          <LevelPill level={currentLevel} />
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Streak</span>
            <StreakDots streak={streak} />
          </div>
        </div>

        <QuestionCard question={currentQuestion} />

        <AnswerInput
          value={answer}
          onChange={setAnswer}
          onKeyDown={onKeyDown}
          onSubmit={handleSubmit}
          submitting={submitting}
          inputRef={inputRef}
        />
      </SessionLayout>
    );
  }

  // ── DIGITAL NOTEBOOK REVIEW ───────────────────────────────────────────────────
  if (phase === "show_review") {
    const tmpl = lastResult?.review_template;
    const content = (tmpl && DNR[tmpl]) || DEFAULT_DNR;
    const wrongQ = currentQuestion;

    // Determine if "Solve with Mitraa" button is eligible
    const canSolveWithMitraa = (
      wrongQ &&
      wrongQ.operand_1 != null &&
      wrongQ.operand_2 != null &&
      wrongQ.operator != null &&
      !wrongQ.level?.endsWith("0") &&
      // Division: suppress if remainder exists
      !(wrongQ.operator === "÷" && wrongQ.operand_1 % wrongQ.operand_2 !== 0) &&
      // Multiplication: Phase 1 = single-digit multiplier only
      !(wrongQ.operator === "x" && wrongQ.operand_2 >= 10)
    );

    return (
      <SessionLayout skill={decodedSkill} symbol={symbol} onBack={() => nav("/math-foundations")}>
        <div className="mb-4 text-xs font-bold text-red-500 flex items-center gap-1.5">
          <XCircle className="w-4 h-4" /> Not quite — let's review
        </div>

        {wrongQ && (
          <div className="mb-4 p-4 rounded-2xl bg-red-50 border border-red-100">
            <p className="text-sm text-slate-600">{wrongQ.question_text}</p>
            <p className="text-sm font-semibold text-red-600 mt-1">Correct answer: {lastResult?.correct_answer}</p>
          </div>
        )}

        {/* Notebook card */}
        <div className="rounded-3xl border border-fuchsia-200 overflow-hidden mb-4">
          <div className="bg-fuchsia-600 px-5 py-3 flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-white" />
            <span className="text-sm font-bold text-white">Digital Notebook — {content.title}</span>
          </div>
          <div className="bg-fuchsia-50 px-5 py-5">
            <p className="text-slate-700 text-sm leading-relaxed">{content.tip}</p>
          </div>
        </div>

        {/* Solve with Mitraa button */}
        {canSolveWithMitraa && (
          <Button
            variant="outline"
            onClick={() => setShowNotebook(true)}
            className="w-full rounded-full border-violet-300 text-violet-700 hover:bg-violet-50 mb-4 py-3"
            data-testid="solve-with-mitraa-btn"
          >
            Solve with Mitraa
          </Button>
        )}

        <Button
          onClick={proceedFromReview}
          className="w-full rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white py-3"
          data-testid="fm-review-continue-btn"
        >
          Got it — try the next one
        </Button>

        {wrongQ?.record_id && sessionId && (
          <QuestionFlagTrigger
            recordId={wrongQ.record_id}
            collection="fm_questions"
            sessionId={sessionId}
          />
        )}

        {/* Solve with Mitraa overlay */}
        {showNotebook && canSolveWithMitraa && (
          <SolveWithMitraa
            operand1={wrongQ.operand_1}
            operand2={wrongQ.operand_2}
            operation={wrongQ.operator}
            answer={wrongQ.answer}
            onClose={() => setShowNotebook(false)}
          />
        )}
      </SessionLayout>
    );
  }

  // ── LEVEL UP ──────────────────────────────────────────────────────────────────
  if (phase === "level_up") {
    return (
      <TransitionScreen
        icon={<TrendingUp className="w-12 h-12 text-emerald-500" />}
        title="Level Up!"
        message={`Great work! You've advanced to ${lastResult?.new_level || currentLevel}.`}
        color="emerald"
        onContinue={proceedFromTransition}
        continueLabel="Keep going"
      />
    );
  }

  // ── LEVEL DOWN ────────────────────────────────────────────────────────────────
  if (phase === "level_down") {
    return (
      <TransitionScreen
        icon={<TrendingDown className="w-12 h-12 text-amber-500" />}
        title="Let's step back"
        message={`We'll practise ${lastResult?.new_level || currentLevel} for a bit. You've got this!`}
        color="amber"
        onContinue={proceedFromTransition}
        continueLabel="Let's go"
      />
    );
  }

  // ── L0 GATE RETRY ─────────────────────────────────────────────────────────────
  if (phase === "l0_gate_retry") {
    return (
      <TransitionScreen
        icon={<RotateCcw className="w-12 h-12 text-sky-500" />}
        title="Back to Basics"
        message="Let's revisit the foundation questions — you'll get it this time!"
        color="sky"
        onContinue={startL0FromRetry}
        continueLabel="Try the gate again"
      />
    );
  }

  return null;
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function SessionLayout({ skill, symbol, onBack, children }) {
  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 py-8" data-testid="fm-session-runner">
      <div className="flex items-center justify-between mb-8">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-fuchsia-600 transition-colors"
          data-testid="fm-session-back"
        >
          <ArrowLeft className="w-4 h-4" /> {skill}
        </button>
        <span className="text-2xl font-black text-fuchsia-200 select-none">{symbol}</span>
      </div>
      {children}
    </div>
  );
}

function QuestionCard({ question }) {
  if (!question) return null;
  return (
    <div
      className="rounded-3xl bg-white border border-slate-200 shadow-sm p-8 mb-6"
      data-testid="fm-question-card"
    >
      <p
        className="text-slate-800 text-xl sm:text-2xl font-semibold leading-snug"
        data-testid="fm-question-text"
      >
        {question.question_text}
      </p>
    </div>
  );
}

function AnswerInput({ value, onChange, onKeyDown, onSubmit, submitting, inputRef }) {
  return (
    <div className="flex gap-3">
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Type your answer…"
        className="flex-1 rounded-2xl border-2 border-slate-200 focus:border-fuchsia-400 outline-none px-5 py-3 text-lg font-semibold text-slate-800 bg-white transition-colors"
        data-testid="fm-answer-input"
        disabled={submitting}
        autoComplete="off"
      />
      <Button
        onClick={onSubmit}
        disabled={!value.trim() || submitting}
        className="rounded-2xl bg-fuchsia-600 hover:bg-fuchsia-700 text-white px-6 py-3 font-bold text-base transition-all disabled:opacity-40"
        data-testid="fm-submit-btn"
      >
        {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Submit"}
      </Button>
    </div>
  );
}

function TerminalScreen({ icon, title, message, color, children }) {
  const colorMap = {
    red: "bg-red-50 border-red-200",
    amber: "bg-amber-50 border-amber-200",
    slate: "bg-slate-50 border-slate-200",
    emerald: "bg-emerald-50 border-emerald-200",
  };
  return (
    <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="fm-terminal-screen">
      <div className={`rounded-3xl p-10 border ${colorMap[color] || colorMap.slate}`}>
        <div className="flex justify-center mb-4">{icon}</div>
        <h2 className="font-display text-2xl font-bold text-slate-800 mb-2">{title}</h2>
        <p className="text-slate-500 text-sm mb-6">{message}</p>
        {children}
      </div>
    </div>
  );
}

function TransitionScreen({ icon, title, message, color, onContinue, continueLabel }) {
  const colorMap = {
    emerald: "bg-emerald-50 border-emerald-200",
    amber: "bg-amber-50 border-amber-200",
    sky: "bg-sky-50 border-sky-200",
    red: "bg-red-50 border-red-200",
  };
  const btnMap = {
    emerald: "bg-emerald-500 hover:bg-emerald-600 text-white",
    amber: "bg-amber-500 hover:bg-amber-600 text-white",
    sky: "bg-sky-500 hover:bg-sky-600 text-white",
    red: "bg-red-500 hover:bg-red-600 text-white",
  };
  return (
    <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="fm-transition-screen">
      <div className={`rounded-3xl p-10 border ${colorMap[color] || colorMap.amber}`}>
        <div className="flex justify-center mb-4">{icon}</div>
        <h2 className="font-display text-2xl font-bold text-slate-800 mb-2">{title}</h2>
        <p className="text-slate-500 text-sm mb-6">{message}</p>
        <Button
          onClick={onContinue}
          className={`rounded-full px-8 ${btnMap[color] || btnMap.amber}`}
          data-testid="fm-transition-continue-btn"
        >
          {continueLabel || "Continue"}
        </Button>
      </div>
    </div>
  );
}
